import api from "./axios";


// ============================================================
// HELPER
// ============================================================

const cleanParams = (params = {}) => {

    return Object.fromEntries(
        Object.entries(params).filter(
            ([, value]) =>
                value !== "" &&
                value !== null &&
                value !== undefined
        )
    );

};


// ============================================================
// LIST VISITS
// ============================================================
//
// GET /visits
//
// Existing visit listing flow.
//
// ============================================================

export const getVisits = async (
    page = 1,
    limit = 10,
    filters = {}
) => {

    const response =
        await api.get(
            "/visits",
            {
                params: {
                    page,
                    limit,
                    ...cleanParams(filters),
                },
            }
        );

    return response.data;

};


// ============================================================
// VISIT OVERVIEW
// ============================================================
//
// GET /visits/overview
//
// Returns salesperson-wise summary:
//
// - Salesperson
// - Number of visits
// - Total KM
// - Locations
// - Total expense
// - Approval status
//
// Example:
//
// getVisitOverview({
//     visitDate: "2026-09-30",
//     employeeId: "18"
// });
//
// ============================================================

export const getVisitOverview = async (
    filters = {}
) => {

    const response =
        await api.get(
            "/visits/overview",
            {
                params:
                    cleanParams(
                        filters
                    ),
            }
        );

    return response.data;

};


// ============================================================
// SALESPERSON OVERVIEW
// ============================================================
//
// GET /visits/overview/:employeeId
//
// Used for salesperson-specific daily/detail view.
//
// ============================================================

export const getVisitOverviewByEmployee =
    async (
        employeeId,
        filters = {}
    ) => {

        if (!employeeId) {

            throw new Error(
                "Employee ID is required."
            );

        }


        const response =
            await api.get(
                `/visits/overview/${employeeId}`,
                {
                    params:
                        cleanParams(
                            filters
                        ),
                }
            );


        return response.data;

    };


// ============================================================
// SINGLE VISIT
// ============================================================
//
// GET /visits/:id
//
// Returns complete Visit:
//
// - Visit information
// - Employee
// - School / Party
// - Journey Plan
// - Attendance
// - Working data
// - People met
// - Photos
// - Followups
// - Samples
// - Orders
// - Payments
// - Expenses
// - Approval information
//
// ============================================================

export const getVisit = async (
    id
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    const response =
        await api.get(
            `/visits/${id}`
        );


    return response.data;

};


// ============================================================
// VISIT ROUTE
// ============================================================
//
// GET /visits/:id/route
//
// Returns actual recorded route:
//
// - Start point
// - Tracking points
// - End point
// - Sequence
// - Latitude
// - Longitude
// - Address
// - Point-to-point KM
// - Total KM
//
// ============================================================

export const getVisitRoute = async (
    id
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    const response =
        await api.get(
            `/visits/${id}/route`
        );


    return response.data;

};


// ============================================================
// CREATE VISIT
// ============================================================
//
// POST /visits
//
// Journey Plan does NOT automatically create Visit.
//
// Actual Visit is created separately.
//
// ============================================================

export const createVisit = async (
    data
) => {

    const response =
        await api.post(
            "/visits",
            data
        );


    return response.data;

};


// ============================================================
// UPDATE VISIT
// ============================================================
//
// PATCH /visits/:id
//
// Existing edit/correction flow.
//
// ============================================================

export const updateVisit = async (
    id,
    data
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    const response =
        await api.patch(
            `/visits/${id}`,
            data
        );


    return response.data;

};


// ============================================================
// START VISIT
// ============================================================

export const startVisit = async (
    id,
    data
) => {

    if (!id) {
        throw new Error(
            "Visit ID is required."
        );
    }

    if (
        data?.latitude === undefined ||
        data?.latitude === null ||
        data?.latitude === ""
    ) {
        throw new Error(
            "Latitude is required."
        );
    }

    if (
        data?.longitude === undefined ||
        data?.longitude === null ||
        data?.longitude === ""
    ) {
        throw new Error(
            "Longitude is required."
        );
    }

    const response =
        await api.post(
            `/visits/${id}/start`,
            {
                // IMPORTANT:
                // Backend expects number strings
                latitude:
                    String(
                        data.latitude
                    ),

                longitude:
                    String(
                        data.longitude
                    ),

                ...(data?.address
                    ? {
                        address:
                            String(
                                data.address
                            ),
                    }
                    : {}),
            }
        );

    return response.data;
};


// ============================================================
// ADD VISIT ROUTE POINT
// ============================================================

export const addVisitRoutePoint = async (
    id,
    data
) => {

    if (!id) {
        throw new Error(
            "Visit ID is required."
        );
    }

    if (
        data?.latitude === undefined ||
        data?.latitude === null ||
        data?.latitude === ""
    ) {
        throw new Error(
            "Latitude is required."
        );
    }

    if (
        data?.longitude === undefined ||
        data?.longitude === null ||
        data?.longitude === ""
    ) {
        throw new Error(
            "Longitude is required."
        );
    }

    const latitude =
        String(
            data.latitude
        );

    const longitude =
        String(
            data.longitude
        );


    const allowedPointTypes = [
        "start",
        "tracking",
        "end",
    ];


    const pointType =
        data?.pointType &&
        allowedPointTypes.includes(
            data.pointType
        )
            ? data.pointType
            : "tracking";


    const payload = {

        // IMPORTANT:
        // Backend expects number strings
        latitude,

        longitude,

        pointType,

    };


    if (
        data?.address &&
        String(
            data.address
        ).trim()
    ) {

        payload.address =
            String(
                data.address
            ).trim();

    }


    console.log(
        "ROUTE POINT PAYLOAD:",
        payload
    );


    const response =
        await api.post(
            `/visits/${id}/route-points`,
            payload
        );


    return response.data;
};


// ============================================================
// SUBMIT VISIT
// ============================================================
//
// POST /visits/:id/submit
//
// IMPORTANT:
//
// Visits.jsx now saves the "end" route point BEFORE calling
// this API.
//
// Flow:
//
// GPS
//  ↓
// addVisitRoutePoint(pointType = "end")
//  ↓
// success
//  ↓
// submitVisit()
//
// ============================================================

export const submitVisit = async (
    id
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    const response =
        await api.post(
            `/visits/${id}/submit`
        );


    return response.data;

};


// ============================================================
// APPROVE VISIT
// ============================================================
//
// POST /visits/:id/approve
//
// ============================================================

export const approveVisit = async (
    id
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    const response =
        await api.post(
            `/visits/${id}/approve`
        );


    return response.data;

};


// ============================================================
// REJECT VISIT
// ============================================================
//
// POST /visits/:id/reject
//
// ============================================================

export const rejectVisit = async (
    id,
    reason
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    if (
        !reason ||
        !reason.trim()
    ) {

        throw new Error(
            "Rejection reason is required."
        );

    }


    const response =
        await api.post(
            `/visits/${id}/reject`,
            {
                reason:
                    reason.trim(),
            }
        );


    return response.data;

};


// ============================================================
// REQUEST CORRECTION
// ============================================================
//
// POST /visits/:id/request-correction
//
// ============================================================

export const requestVisitCorrection =
    async (
        id,
        reason
    ) => {

        if (!id) {

            throw new Error(
                "Visit ID is required."
            );

        }


        if (
            !reason ||
            !reason.trim()
        ) {

            throw new Error(
                "Correction reason is required."
            );

        }


        const response =
            await api.post(
                `/visits/${id}/request-correction`,
                {
                    reason:
                        reason.trim(),
                }
            );


        return response.data;

    };


// ============================================================
// DELETE / CANCEL VISIT
// ============================================================
//
// DELETE /visits/:id
//
// ============================================================

export const deleteVisit = async (
    id
) => {

    if (!id) {

        throw new Error(
            "Visit ID is required."
        );

    }


    const response =
        await api.delete(
            `/visits/${id}`
        );


    return response.data;

};

// ============================================================
// GET NEXT VISIT NUMBER
// ============================================================

export const getNextVisitNumber = async ({
    visitType = "",
    schoolId = "",
    partyName = "",
} = {}) => {
    const response = await api.get(
        "/visits/next-number",
        {
            params: cleanParams({
                visitType,
                schoolId,
                partyName,
            }),
        }
    );

    return response.data;
};


// ============================================================
// VISIT HISTORY
// ============================================================

export const getVisitHistory = async ({
    schoolId = "",
    partyName = "",
} = {}) => {
    const response = await api.get(
        "/visits/history",
        {
            params: cleanParams({
                schoolId,
                partyName,
            }),
        }
    );

    return response.data;
};


// ============================================================
// DEFAULT EXPORT
// ============================================================

const visitApi = {

    // Visit list
    getVisits,

    // Overview
    getVisitOverview,
    getVisitOverviewByEmployee,

    // Individual visit
    getVisit,

    // Route
    getVisitRoute,
    addVisitRoutePoint,

    // CRUD
    createVisit,
    updateVisit,

     // Visit number & history
    getNextVisitNumber,
    getVisitHistory,

    // Workflow
    startVisit,
    submitVisit,
    approveVisit,
    rejectVisit,
    requestVisitCorrection,
    deleteVisit,

};


export default visitApi;